import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const formatError = (detail) => {
    if (!detail) return "Invalid credentials";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map(item => item?.msg || JSON.stringify(item)).join(" ");
    return detail?.msg || String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      const next = searchParams.get("next") || localStorage.getItem("vi_return_to") || "/";
      localStorage.removeItem("vi_return_to");
      navigate(next.startsWith("/") ? next : "/", { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const isExpired = searchParams.get("session") === "expired";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4" data-testid="login-page">
      <Card className="w-full max-w-md rounded-xl border bg-card shadow-lg">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <img src="/varyynt-logo-wide.svg" alt="Varyynt" className="h-10 w-auto mx-auto mb-3" />
            <p className="text-sm text-slate-500 font-accent">Talent intelligence for pharma & biotech</p>
          </div>
          {isExpired && (
            <div data-testid="login-session-expired-alert" className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Your session expired for security. Please sign in again to continue.
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-evergreen-950 hover:bg-evergreen-900 text-white"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
          <p className="text-center text-sm text-slate-500 mt-4">
            <a href="/forgot-password" className="text-slate-500 hover:text-slate-700">Forgot password?</a>
            <span className="mx-2 text-slate-300">|</span>
            Have an invite?{" "}
            <a href="/register" className="text-teal-600 hover:text-teal-700 font-medium">Register here</a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
